'use client';

import clsx from 'clsx';
import { useState } from 'react';
import { Modal, Typography, useI18n, useQueryState } from '@ywwwtseng/react-kit';
import Image from 'next/image';
import { Confirm } from '@ywwwtseng/react-kit';
import { useWalletKitConnect } from '@ywwwtseng/wallet-kit';
import { useTonWalletKit } from '@ywwwtseng/ton-wallet-kit';
import { Disconnect } from '@/components/icons';
import { shortenAddress } from '@/utils/app';
import { NETWORKS, NETWORK_NAMES } from '@/constants';

export function WalletConnectModal({
  open,
  onCloseAction,
}: {
  open: boolean;
  onCloseAction: () => void;
}) {
  const networks = useQueryState<string[]>('networks');
  const { t } = useI18n();
  const walletKitConnect = useWalletKitConnect();
  const tonWalletKit = useTonWalletKit();
  const [disconnectModal, setDisconnectModal] = useState<{
    open: boolean;
    chain?: string | undefined;
  }>({
    open: false,
  });

  const onDisconnect = (chain: string) => {
    void setDisconnectModal({ open: true, chain });
  };

  const onChainSelected = (chain: string) => {
    if (chain === 'evm' || chain === 'solana') {
      void walletKitConnect.open();
    }

    if (chain === 'ton') {
      void tonWalletKit.connect();
    }
  };

  if (!networks) {
    return null;
  }

  return (
    <>
      <Modal
        title="wallet connect modal"
        classes={{ content: 'border-1 border-white/10' }}
        open={open}
        onClose={onCloseAction}
      >
        <Typography size="1">{t('wallet_connect_modal.title')}</Typography>
        <div className="flex flex-col justify-center items-center h-full w-full gap-4 pb-4 px-4">
          {networks.includes(NETWORKS.TON) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (tonWalletKit.address) {
                  onDisconnect?.('ton');
                } else {
                  onChainSelected('ton');
                }

                onCloseAction();
              }}
            >
              <div className="flex items-center gap-2">
                <Image
                  src="/images/chains/ton.png"
                  alt="ton"
                  width={32}
                  height={32}
                />
              </div>
              {tonWalletKit.address ? (
                <div className="flex items-center gap-2">
                  <Typography size="1" weight={500}>
                    {shortenAddress(tonWalletKit.address)}
                  </Typography>
                  <Disconnect width={16} height={16} />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Typography size="1" weight={500}>
                    {NETWORK_NAMES[NETWORKS.TON]}
                  </Typography>
                </div>
              )}
            </div>
          )}
          {networks.includes(NETWORKS.SOLANA) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (walletKitConnect.accounts.solana.address) {
                  onDisconnect?.('solana');
                } else {
                  onChainSelected('solana');
                }

                onCloseAction();
              }}
            >
              <div className="flex items-center gap-2">
                <Image
                  src="/images/chains/solana.png"
                  className="rounded-full"
                  alt="evm"
                  width={32}
                  height={32}
                />
              </div>
              {walletKitConnect.accounts.solana.address ? (
                <div className="flex items-center gap-2">
                  <Typography size="1" weight={500}>
                    {shortenAddress(walletKitConnect.accounts.solana.address)}
                  </Typography>
                  <Disconnect width={16} height={16} />
                </div>
              ) : (
                <Typography size="1" weight={500}>
                  {NETWORK_NAMES[NETWORKS.SOLANA]}
                </Typography>
              )}
            </div>
          )}

          {(networks.includes(NETWORKS.ETHEREUM) || networks.includes(NETWORKS.BSC)) && (
            <div
              className="flex items-center justify-between w-full"
              onClick={() => {
                if (walletKitConnect.accounts.ethereum.address) {
                  onDisconnect?.('evm');
                } else {
                  onChainSelected('evm');
                }

                onCloseAction();
              }}
            >
              <div className="flex items-center">
                {networks.includes(NETWORKS.ETHEREUM) && (
                  <Image
                    src="/images/chains/ethereum.png"
                    alt="ethereum"
                    width={32}
                    height={32}
                  />
                )}
                {networks.includes(NETWORKS.BSC) && (
                  <Image
                    className={clsx({ 'translate-x-[-50%]': networks.includes(NETWORKS.ETHEREUM) })}
                    src="/images/chains/bsc.png"
                    alt="bsc"
                    width={32}
                    height={32}
                  />
                )}
              </div>
              {walletKitConnect.accounts.ethereum.address ? (
                <div className="flex items-center gap-2">
                  <Typography size="1" weight={500}>
                    {shortenAddress(walletKitConnect.accounts.ethereum.address)}
                  </Typography>
                  <Disconnect width={16} height={16} />
                </div>
              ) : (
                <Typography size="1" weight={500}>
                  {(networks.includes(NETWORKS.ETHEREUM) && networks.includes(NETWORKS.BSC))
                    ? 'EVM Networks'
                    : networks.includes(NETWORKS.ETHEREUM)
                      ? NETWORK_NAMES[NETWORKS.ETHEREUM]
                      : NETWORK_NAMES[NETWORKS.BSC]}
                </Typography>
              )}
            </div>
          )}

        </div>
      </Modal>
      <Confirm
        classes={{
          content: 'text-xs',
        }}
        open={disconnectModal.open}
        onOpenChange={(open) => {
          void setDisconnectModal({ open });
        }}
        title={t('disconnect_modal.title')}
        description={t('disconnect_modal.description')}
        cancel={{
          children: t('common.cancel'),
        }}
        confirm={{
          children: t('common.disconnect'),
          onClick: () => {
            if (
              disconnectModal.chain === 'evm' ||
              disconnectModal.chain === 'solana'
            ) {
              void walletKitConnect.disconnect(true);
            }

            if (disconnectModal.chain === 'ton') {
              void tonWalletKit.disconnect();
            }
          },
        }}
      />
    </>
  );
}
